import { Fragment, useContext, useMemo } from 'react';
import {
    Badge, Col, FormGroup, ListGroupItem, ListGroupItemHeading, Row, UncontrolledTooltip,
} from 'reactstrap';
import { isEmpty, orderBy, reduce } from 'lodash-es';
import moment from 'moment';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFlag, faTrafficLight, faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { Hide } from '@s20/components/common/Hide';
import { DispatchContext } from '../../DispatchContext';
import { AppNavPaths } from '../../../../Common';
import { EmployeeMetrics } from '../../../../employee/EmployeeMetrics';
import {
    Flex, FlexStartRow, FormLabel,
} from '../../../../common/forms/FormElements';
import { INTERPUNCT } from '../../../../common/util';
import { AccordionCollapse } from '../../../../common/AccordionCollapse';
import { ResourceChip } from './ResourceChip';
import { PhoneNumber } from './PhoneNumber';

export const Resource = ({ isSelected, setSelected, ...resource }) => {
    const { certifications } = useContext(DispatchContext);

    const employeeCertifications = useMemo(() =>
        resource.certificationIds.map((id) => certifications.find((cert) => cert.id === id))
            .filter(Boolean),
        [certifications, resource.certificationIds],
    );

    const missingCertifications = useMemo(() => certifications.filter((cert) =>
        cert.mustBeValidForDispatch && !resource.certificationIds.includes(cert.id),
    ), [certifications, resource.certificationIds]);

    const hours = useMemo(() => reduce(resource.hours, (results, entry) => {
        const dateString = moment(entry.jobDate).format();
        if (!results[dateString]) {
            return {
                ...results,
                [dateString]: entry.value,
            };
        }

        return {
            ...results,
            [dateString]: results[dateString] + entry.value,
        };
    }, {}), [resource.hours]);

    return (
        <ListGroupItem
            key={resource.id}
            className={clsx('mb-4 employee-item rounded-lg w-100', isSelected && 'selected')}
            action
            onClick={() => setSelected(resource)}
        >
            <Flex>
                <div className="flex-fill">
                    <Flex
                        component={ListGroupItemHeading}
                        alignItems="center"
                        useColumnOnMobile={false}
                    >
                        <ResourceChip
                            isSelected={isSelected}
                            {...resource}
                        />

                        {resource.employeeName ? (
                            <Link
                                to={`${AppNavPaths.Employee}/${resource.id}`}
                                target="_blank"
                                className="site-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                {resource.employeeName}
                            </Link>
                        ) : (
                            <Link
                                to={`${AppNavPaths.Equipment}?eId=${resource.id}`}
                                target="_blank"
                                className="site-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                {`${resource.equipmentTypeName} ${INTERPUNCT} ${resource.equipmentName}`}
                            </Link>
                        )}

                        <Badge
                            color="warning"
                            className="ml-2"
                        >
                            {resource.dispatchCompanyName}
                        </Badge>
                    </Flex>

                    <Row>
                        <Col xs={6}>
                            <FormGroup>
                                <FlexStartRow>
                                    {resource.distance > 0 ? (
                                        <>
                                            <strong className="mr-1">{resource.distanceInMiles}</strong>

                                            <span>
                                                mi to
                                            </span>

                                            {resource.equipmentTypeName ? <span className="ml-1">port</span> : (
                                                <Badge
                                                    color="secondary"
                                                    className="ml-1"
                                                >
                                                    M
                                                </Badge>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {resource.equipmentTypeName ? 'At dispatch port' : 'No location'}
                                        </>
                                    )}
                                </FlexStartRow>

                                {Boolean(resource.isFlaggerPreferred) && (
                                    <div>
                                        <Badge color="info">
                                            Preferred
                                        </Badge>
                                    </div>
                                )}
                            </FormGroup>

                            {(employeeCertifications.length > 0 || missingCertifications.length > 0) && (
                                <FormGroup>
                                    <FormLabel
                                        label="Certifications"
                                    />

                                    <div>
                                        {employeeCertifications.map((cert) => (
                                            <span key={cert.id}>
                                                <Badge
                                                    id={`cert-${cert.id}`}
                                                    color="primary"
                                                    className="mr-2"
                                                >
                                                    {cert.abbreviation}
                                                </Badge>

                                                <UncontrolledTooltip
                                                    target={`cert-${cert.id}`}
                                                >
                                                    {cert.name}
                                                </UncontrolledTooltip>
                                            </span>
                                        ))}

                                        {missingCertifications.map((cert) => (
                                            <Fragment key={cert.id}>
                                                <Badge
                                                    id={`missing-cert-${cert.id}`}
                                                    color="danger"
                                                    className="mr-2"
                                                >
                                                    {cert.abbreviation}
                                                </Badge>

                                                <UncontrolledTooltip
                                                    target={`missing-cert-${cert.id}`}
                                                >
                                                    {cert.name}
                                                </UncontrolledTooltip>
                                            </Fragment>
                                        ))}
                                    </div>
                                </FormGroup>
                            )}

                            {Boolean(resource.linkedEmployee) && (
                                <FormGroup>
                                    <FormLabel label="Linked Employee" />

                                    <Link
                                        to={`${AppNavPaths.Employee}/${resource.linkedEmployee.id}`}
                                        target="_blank"
                                        className="ml-1"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                    >
                                        {resource.linkedEmployee.fullName}
                                    </Link>
                                </FormGroup>
                            )}

                            {resource.equipment.length > 0 && (
                                <FormGroup>
                                    <FormLabel
                                        label="Equipment"
                                    />

                                    {orderBy(resource.equipment, (eq) => eq.equipmentTypeId).map((eq) => {
                                        let icon = faFlag;

                                        if (eq.isVehicle) {
                                            icon = faTruck;
                                        } else if (eq.isAFAD) {
                                            icon = faTrafficLight;
                                        }

                                        return (
                                            <div key={eq.identifier}>
                                                <Link
                                                    to={`${AppNavPaths.Equipment}?eId=${eq.id}`}
                                                    target="_blank"
                                                    className="ml-1"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={icon}
                                                        className="mr-1"
                                                    />

                                                    <span>{eq.identifier}</span>
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </FormGroup>
                            )}
                        </Col>

                        <Col xs={6}>
                            {!isEmpty(hours) && (
                                <FormGroup>
                                    <FormLabel text="Hours" />

                                    <div>
                                        {Object.entries(hours).map(([date, value]) => (
                                            <FlexStartRow key={date}>
                                                <Badge
                                                    color="secondary"
                                                    className="mr-1"
                                                >
                                                    {moment(date).format('dd')}
                                                </Badge>

                                                <span>{value}</span>
                                            </FlexStartRow>
                                        ))}
                                    </div>
                                </FormGroup>
                            )}
                        </Col>
                    </Row>

                    <Hide smDown>
                        <div>
                            <PhoneNumber phoneNumber={resource.phoneNumber} />
                        </div>
                    </Hide>
                </div>

                {Boolean(resource.metrics) && (
                    <>
                        <Hide mdUp>
                            <EmployeeMetrics metrics={resource.metrics} />
                        </Hide>

                        <Hide smDown>
                            <AccordionCollapse title="View More">
                                <div className="pt-2 pb-2">
                                    <EmployeeMetrics metrics={resource.metrics} />
                                </div>
                            </AccordionCollapse>
                        </Hide>
                    </>
                )}
            </Flex>

            <Hide mdUp>
                <Flex>
                    <PhoneNumber phoneNumber={resource.phoneNumber} />
                </Flex>
            </Hide>
        </ListGroupItem>
    );
};